import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, UserCog, Shield, ShieldPlus, Palette } from "lucide-react";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { RoleBadge, BADGE_ICONES, BADGE_POLICES } from "../components/RoleBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const CAT_LABELS = {
  sst: "SST",
  ssiap: "SSIAP",
  carte_pro: "Cartes Pro",
  incendie: "Formations Incendie",
  sessions: "Sessions",
  rappels: "Rappels email",
  actualites: "Actualités",
};

const ACTION_LABELS = {
  voir: "Voir",
  creer: "Créer",
  modifier: "Modifier",
  supprimer: "Supprimer",
  export: "Exporter",
  envoyer: "Envoyer",
  configurer: "Configurer",
  publier: "Publier",
};

const EMPTY_USER = { email: "", name: "", password: "", role: "lecteur" };
const EMPTY_ROLE = { nom: "", permissions: {}, badge: {} };

function PermissionsMatrix({ categories, permissions, onToggle, prefix }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={`${prefix}-matrix`}>
      {Object.entries(categories).map(([cat, actions], i) => (
        <div key={cat} className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 ${i % 2 ? "bg-secondary/30" : ""}`}>
          <span className="w-40 shrink-0 text-xs font-semibold">{CAT_LABELS[cat] || cat}</span>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {actions.map((a) => (
              <label key={a} className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={(permissions[cat] || []).includes(a)}
                  onCheckedChange={(v) => onToggle(cat, a, !!v)}
                  data-testid={`${prefix}-perm-${cat}-${a}`}
                />
                {ACTION_LABELS[a] || a}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState(EMPTY_ROLE);
  const [savingRole, setSavingRole] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/users"), api.get("/roles")])
      .then(([u, r]) => {
        setUsers(u.data.users);
        setRoles(r.data.roles);
        setCategories(r.data.categories);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (me?.role !== "admin") {
    return (
      <div className="rounded-xl border border-red-800 bg-red-950/30 p-10 text-center" data-testid="users-forbidden">
        <p className="font-semibold text-red-400">Accès réservé à l'administrateur</p>
      </div>
    );
  }

  const openDialog = (u) => {
    setEditing(u || null);
    setForm(u ? { email: u.email, name: u.name, password: "", role: u.role } : EMPTY_USER);
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utilisateur mis à jour");
      } else {
        await api.post("/users", form);
        toast.success(`Compte créé pour ${form.email}`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.email} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openRoleDialog = (r) => {
    setEditingRole(r || null);
    setRoleForm(r ? { nom: r.nom, permissions: r.permissions || {}, badge: r.badge || {} } : EMPTY_ROLE);
    setRoleDialogOpen(true);
  };

  const setBadge = (key, value) => {
    setRoleForm((f) => {
      const badge = { ...(f.badge || {}) };
      if (value === "" || value === false || value === undefined) delete badge[key];
      else badge[key] = value;
      return { ...f, badge };
    });
  };

  const togglePerm = (cat, action, checked) => {
    setRoleForm((f) => {
      const current = new Set(f.permissions[cat] || []);
      if (checked) current.add(action); else current.delete(action);
      return { ...f, permissions: { ...f.permissions, [cat]: [...current] } };
    });
  };

  const submitRole = async () => {
    setSavingRole(true);
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, roleForm);
        toast.success(`Rôle « ${editingRole.nom} » mis à jour`);
      } else {
        await api.post("/roles", roleForm);
        toast.success(`Rôle « ${roleForm.nom} » créé`);
      }
      setRoleDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingRole(false);
    }
  };

  const removeRole = async (r) => {
    if (!window.confirm(`Supprimer le rôle « ${r.nom} » ?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Rôle supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const permSummary = (perms) => {
    const parts = Object.entries(perms || {})
      .filter(([, acts]) => acts.length > 0)
      .map(([cat, acts]) => `${CAT_LABELS[cat] || cat} (${acts.map((a) => ACTION_LABELS[a] || a).join(", ")})`);
    return parts.length ? parts.join(" · ") : "Aucune permission";
  };

  return (
    <div className="space-y-10" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Accès au site</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Utilisateurs & Rôles</h1>
          <p className="text-sm text-muted-foreground mt-1">Créez des rôles sur mesure avec des permissions par catégorie, puis assignez-les aux comptes.</p>
        </div>
        <Button onClick={() => openDialog(null)} data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-2" /> Ajouter un utilisateur
        </Button>
      </div>

      <section className="space-y-4" data-testid="roles-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><Shield className="h-5 w-5 text-accent" /> Rôles personnalisés</h2>
          <Button variant="outline" onClick={() => openRoleDialog(null)} data-testid="add-role-button">
            <ShieldPlus className="h-4 w-4 mr-2" /> Nouveau rôle
          </Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {roles.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4 space-y-2" data-testid={`role-card-${r.nom}`}>
              <div className="flex items-center justify-between gap-2">
                <RoleBadge role={r.nom} badge={r.badge} />
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openRoleDialog(r)} data-testid={`edit-role-${r.nom}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="hover:text-red-400" onClick={() => removeRole(r)} data-testid={`delete-role-${r.nom}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid={`role-perms-${r.nom}`}>{permSummary(r.permissions)}</p>
            </div>
          ))}
          {!loading && roles.length === 0 && (
            <p className="text-sm text-muted-foreground md:col-span-2" data-testid="roles-empty">Aucun rôle personnalisé. Créez-en un pour définir des accès précis par catégorie.</p>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          <span className="text-blue-400 font-semibold">Admin</span> : accès complet, non modifiable · Les rôles ci-dessus définissent exactement ce que leurs utilisateurs peuvent voir et faire.
        </p>
      </section>

      <section className="space-y-4" data-testid="users-section">
        <h2 className="text-lg font-bold flex items-center gap-2"><UserCog className="h-5 w-5 text-accent" /> Comptes utilisateurs</h2>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]" data-testid="users-table">
            <thead>
              <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Créé le</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.email}{u.id === me?.id && <span className="ml-2 text-xs text-muted-foreground">(vous)</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.name || "—"}</td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} badge={roles.find((r) => r.nom === u.role)?.badge} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.created_at?.slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(u)} data-testid={`edit-user-${u.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {u.id !== me?.id && (
                      <Button variant="ghost" size="icon" className="hover:text-red-400" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-accent" /> {editing ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle>
            <DialogDescription>{editing ? "Laissez le mot de passe vide pour ne pas le changer." : "Le compte pourra se connecter immédiatement avec cet email et ce mot de passe."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing}
                className="bg-background" data-testid="user-form-email" />
            </div>
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background" data-testid="user-form-name" />
            </div>
            <div className="space-y-2">
              <Label>{editing ? "Nouveau mot de passe (optionnel)" : "Mot de passe * (8 caractères min.)"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-background" data-testid="user-form-password" />
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-background" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin — accès complet</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={r.nom} data-testid={`role-option-${r.nom}`}>{r.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Les rôles se gèrent dans la section « Rôles personnalisés » ci-dessus.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving || !form.email || (!editing && form.password.length < 8)} data-testid="user-form-submit">
              {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer le compte"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[92dvh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldPlus className="h-5 w-5 text-accent" /> {editingRole ? `Modifier le rôle « ${editingRole.nom} »` : "Nouveau rôle"}</DialogTitle>
            <DialogDescription>Cochez les actions autorisées pour chaque catégorie. Tout ce qui n'est pas coché sera masqué et bloqué.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingRole && (
              <div className="space-y-2">
                <Label>Nom du rôle *</Label>
                <Input value={roleForm.nom} onChange={(e) => setRoleForm({ ...roleForm, nom: e.target.value })}
                  placeholder="Ex. Responsable SSIAP" className="bg-background" data-testid="role-form-nom" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Permissions par catégorie</Label>
              <PermissionsMatrix categories={categories} permissions={roleForm.permissions} onToggle={togglePerm} prefix="role-form" />
            </div>
            <div className="space-y-3">
              <Label className="flex items-center gap-2"><Palette className="h-4 w-4 text-accent" /> Badge du rôle</Label>
              <div className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    Couleur
                    <input type="color" value={roleForm.badge?.couleur || "#A78BFA"} onChange={(e) => setBadge("couleur", e.target.value)}
                      className="h-8 w-10 cursor-pointer rounded border border-border bg-transparent p-0.5" data-testid="badge-color" />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox checked={!!roleForm.badge?.gras} onCheckedChange={(v) => setBadge("gras", !!v)} data-testid="badge-gras" /> Gras
                  </label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox checked={!!roleForm.badge?.majuscules} onCheckedChange={(v) => setBadge("majuscules", !!v)} data-testid="badge-majuscules" /> Majuscules
                  </label>
                  <Select value={roleForm.badge?.police || "defaut"} onValueChange={(v) => setBadge("police", v === "defaut" ? "" : v)}>
                    <SelectTrigger className="bg-background h-8 w-44 text-xs" data-testid="badge-police"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="defaut">Police par défaut</SelectItem>
                      {BADGE_POLICES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(BADGE_ICONES).map(([key, Icon]) => (
                    <button key={key} type="button" onClick={() => setBadge("icone", key)}
                      className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-colors ${roleForm.badge?.icone === key ? "border-accent bg-accent/15 text-accent" : "border-border text-muted-foreground hover:border-accent/50"}`}
                      data-testid={`badge-icon-${key}`}>
                      <Icon className="h-4 w-4" />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  Aperçu : <RoleBadge role={roleForm.nom || "Exemple"} badge={roleForm.badge} testid="badge-preview" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Annuler</Button>
            <Button onClick={submitRole} disabled={savingRole || (!editingRole && roleForm.nom.trim().length < 2)} data-testid="role-form-submit">
              {savingRole ? "Enregistrement..." : editingRole ? "Mettre à jour" : "Créer le rôle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
